import { ContractPartnerType, MeterPointChangeState, MeterPointChangeType } from './meterpoint-change-request.types';
import {
  ROUTE_CHANGE_TYPE,
  ROUTE_CONFIRMATION,
  ROUTE_CURRENT_CONTRACT_PARTNER,
  ROUTE_HOME,
  ROUTE_METER_POINT_CHANGE_REQUEST,
  ROUTE_METER_POINTS,
  ROUTE_NEW_CONTRACT_PARTNER,
  ROUTE_PDF_UPLOAD,
  ROUTE_PRODUCTS_AND_DATE,
  ROUTE_SUMMARY,
} from '../../app.constants';
import { IPopupModel, IWizardStep } from '@mwe/models';
import { Router } from '@angular/router';
import { SignalState } from '@shared/service/signal-state-service/signal-state.service';
import { AddressForm } from './03_current_contract_partner/model/occupied_contract_partner.model';
import { getAddressPostCodeAndStreetFormatted } from '@mwe/utils';
import { Observable } from 'rxjs';
import { LegacyUmmeldungStatus } from '../../model/meter-point.model';
import { PlatformLocation } from '@angular/common';
import { IkpPopupService } from '@shared/service/popup/popup.service';

export interface MeterPointChangeTypeOption {
  id: MeterPointChangeType;
  text: string;
}

export const meterPointChangeContractPartnerOptions = [
  {
    id: ContractPartnerType.PRIVATE,
    text: 'meterPoint.meterPointChangeRequest.contractPartnerType.privateperson',
  },
  {
    id: ContractPartnerType.COMPANY,
    text: 'meterPoint.meterPointChangeRequest.contractPartnerType.companyOrOrganisation',
  },
];

export const meterPointChangeOptions = [
  {
    id: MeterPointChangeType.EMPTY_TO_OCCUPIED,
    text: 'meterPoint.meterPointChangeRequest.changeType.emptyToOccupied',
  },
  {
    id: MeterPointChangeType.OCCUPIED_TO_EMPTY,
    text: 'meterPoint.meterPointChangeRequest.changeType.occupiedToEmpty',
  },
  {
    id: MeterPointChangeType.TENANT_CHANGE,
    text: 'meterPoint.meterPointChangeRequest.changeType.tenantChange',
  },
];

export const initialMeterPointChangeRequestState: MeterPointChangeState = {
  stepChangeType: MeterPointChangeType.EMPTY_TO_OCCUPIED,
  currentContractPartner: null,
  meterPoint: null,
  newContractPartner: {
    emptyPartner: null,
    formData: {
      partnerType: ContractPartnerType.PRIVATE,
    },
    alternativeInvoicingAddress: null,
  },
  productAndDate: {
    meterPoints: [],
  },
};

const PREV_LABEL = 'meterPoint.meterPointChangeRequest.wizard.prev';
const NEXT_LABEL = 'meterPoint.meterPointChangeRequest.wizard.next';
const CANCEL_LABEL = 'meterPoint.meterPointChangeRequest.wizard.cancel';
const CLOSE_LABEL = 'meterPoint.meterPointChangeRequest.wizard.close';

function getRoute(childRoute: string): string {
  return `/${ROUTE_METER_POINT_CHANGE_REQUEST}/${childRoute}`;
}

export function getMeterPointChangeRequestSteps(storeState: MeterPointChangeState): IWizardStep[] {
  return [
    {
      titleKey: 'meterPoint.meterPointChangeRequest.title',
      path: getRoute(ROUTE_CHANGE_TYPE),
      nextButtonLabel: NEXT_LABEL,
      prevButtonLabel: CANCEL_LABEL,
      progressBarLabel: 'meterPoint.meterPointChangeRequest.stepChangeType',
    },
    {
      titleKey: meterPointChangeOptions.find(option => option.id === storeState.stepChangeType)?.text,
      path: getRoute(ROUTE_PRODUCTS_AND_DATE),
      nextButtonLabel: NEXT_LABEL,
      prevButtonLabel: PREV_LABEL,
      progressBarLabel: 'meterPoint.meterPointChangeRequest.stepProductsAndDate',
    },
    {
      titleKey: meterPointChangeOptions.find(option => option.id === storeState.stepChangeType)?.text,
      path: getRoute(ROUTE_CURRENT_CONTRACT_PARTNER),
      nextButtonLabel: NEXT_LABEL,
      prevButtonLabel: PREV_LABEL,
      progressBarLabel: 'meterPoint.meterPointChangeRequest.currentContractPartner',
    },
    {
      titleKey: meterPointChangeOptions.find(option => option.id === storeState.stepChangeType)?.text,
      path: getRoute(ROUTE_NEW_CONTRACT_PARTNER),
      nextButtonLabel: NEXT_LABEL,
      prevButtonLabel: PREV_LABEL,
      progressBarLabel: 'meterPoint.meterPointChangeRequest.newContractPartner',
    },
    {
      titleKey: 'meterPoint.meterPointChangeRequest.summary.title',
      subTitleKey: 'meterPoint.meterPointChangeRequest.summary.subtitle',
      path: getRoute(ROUTE_SUMMARY),
      nextButtonLabel: NEXT_LABEL,
      prevButtonLabel: PREV_LABEL,
      hidePrevButton: storeState.meterPoint?.legacyUmmeldungStatus === LegacyUmmeldungStatus.WARTEN_AUF_UNTERSCHRIFT,
      progressBarLabel: 'meterPoint.meterPointChangeRequest.summary.progressBarLabel',
    },
    {
      titleKey: meterPointChangeOptions.find(option => option.id === storeState.stepChangeType)?.text,
      path: getRoute(ROUTE_PDF_UPLOAD),
      nextButtonLabel: 'meterPoint.meterPointChangeRequest.wizard.uploadLater',
      hidePrevButton: true,
      hideFromProgressBar: true,
    },
    {
      path: getRoute(ROUTE_CONFIRMATION),
      nextButtonLabel: CLOSE_LABEL,
      hidePrevButton: true,
      progressBarLabel: 'meterPoint.meterPointChangeRequest.confirmation.progressBarLabel',
    },
  ] as IWizardStep[];
}

export function getZaehlpunktnummerShort(zaehlpunktnummer: string) {
  return zaehlpunktnummer?.length > 13 ? zaehlpunktnummer.slice(0, 5) + '.......' + zaehlpunktnummer.slice(-8) : '';
}

export function closeMeterPointChangeRequestWizard(
  popupService: IkpPopupService,
  router: Router,
  wizardStore: SignalState<MeterPointChangeState>,
  noPopup = false,
  extraAction?: Observable<void>,
) {
  if (noPopup) {
    router.navigate([`/${ROUTE_HOME}/${ROUTE_METER_POINTS}`]);
    wizardStore.reset();
  } else {
    popupService.handleGenericPopup(METERPOINT_CHANGE_REQUEST_WARNING_POPUP);
    const popupSubscription = popupService.events.subscribe(result =>
      result
        ? router
            .navigate([`/${ROUTE_HOME}/${ROUTE_METER_POINTS}`])
            .then(() => wizardStore.reset())
            .then(() => (extraAction ? extraAction.subscribe() : null))
            .then(() => popupSubscription.unsubscribe())
        : popupSubscription.unsubscribe(),
    );
  }
  return true;
}

export function formatAddressForm(address: AddressForm): string {
  return (
    getAddressPostCodeAndStreetFormatted({
      postcode: address?.plz,
      city: address?.city,
      street: address?.street,
      doorNumber: address?.door,
      streetNumber: address?.houseNumberStaircase,
      block: undefined,
      country: undefined,
      id: undefined,
    }) || EMPTY_VALUE
  );
}

export function closeIfInactive(state: MeterPointChangeState, router: Router): void {
  const isInProgress = state?.meterPoint?.legacyUmmeldungStatus === LegacyUmmeldungStatus.WARTEN_AUF_UNTERSCHRIFT;
  if (isInProgress) {
    navigateToMeterpointOverview(router);
  }
}

export function checkPrevDataAvailable(hasPrevStepData: boolean, router: Router, commands?: string[]): void {
  if (!hasPrevStepData) {
    navigateToMeterpointOverview(router, commands);
  }
}

export function navigateToMeterpointOverview(router: Router, commands?: string[]): void {
  router.navigate(commands || [ROUTE_HOME, ROUTE_METER_POINTS]);
}

export function disableBrowserBackButton(platformLocation: PlatformLocation): void {
  history.pushState(null, '', location.href);
  platformLocation.onPopState(() => {
    history.pushState(null, '', `/${ROUTE_HOME}/${ROUTE_METER_POINTS}`);
  });
}

export const METERPOINT_CHANGE_REQUEST_WARNING_POPUP: IPopupModel = {
  id: 'warning-popup',
  titleKey: 'meterPoint.meterPointChangeRequest.wizard.closeWarning.title',
  messageKey: 'meterPoint.meterPointChangeRequest.wizard.closeWarning.message',
  showSubmitButton: true,
  submitButtonKey: 'meterPoint.meterPointChangeRequest.wizard.closeWarning.buttonExit',
  showCancelButton: true,
  cancelButtonKey: 'meterPoint.meterPointChangeRequest.wizard.closeWarning.buttonStay',
  iconColorClass: 'blue',
  iconTypeClass: 'fa fa-question',
};

export const EMPTY_VALUE = '---';
